import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Grid,
  Typography,
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
} from '@mui/material';
import { AppContext } from '../../context/Context';
import { useTranslation } from 'react-i18next';
import { SocketContext } from '../../context/SocketContext';

import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import '../../assets/css/buttons.css';
import {
  PointOfSale as PointOfSaleIcon,
  AddCard as AddCardIcon,
  ExitToApp as ExitToAppIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import WGLogo from '../../assets/img/wingames.png';
import CHLogo from '../../assets/img/ClubHouse_Logo_Light.png';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

function ModalEndShiftAuto() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    hall,
    operator,
    session,
    updateShowModalEndShift,
    updateIsDailyReportCompleted,
    updateIsCardReportCompleted,
    updateIsLoadingScreen,
    isLoadingScreen,
    printer,
    modifyLogin,
    modifyOperator,
    modifyOperatorFull,
    modifyHall,
    updateSession,
  } = useContext(AppContext);

  const hallRef = useRef(hall);

  useEffect(() => {
    hallRef.current = hall;
    socket.emit('checkShiftAndDayNumbers', {
      action: 'checkShiftAndDayNumbers',
      bingoName: hallRef.current,
      printer: printerRef.current,
    });
  }, [hall]);

  const isLoadingScreenRef = useRef(isLoadingScreen);
  useEffect(() => {
    isLoadingScreenRef.current = isLoadingScreen;
  }, [isLoadingScreen]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const { socket } = useContext(SocketContext);

  const [shiftNumbers, setShiftNumbers] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // 'choice' → user picks what to do; 'confirm' → shows numbers + finalises
  const [step, setStep] = useState('choice');
  // 'shift' → close shift only; 'day' → close shift + last of the day
  const [closeType, setCloseType] = useState(null);
  const [cashOnClosing, setCashOnClosing] = useState('');

  useEffect(() => {
    const handleCloseShift = (data) => {
      updateIsLoadingScreen(false);

      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      modifyLogin(false);
      modifyOperator('');
      modifyOperatorFull('');
      modifyHall('');
      updateSession('');
      updateShowModalEndShift(false);
      navigate('/login');
    };

    const handleCheckShiftAndDayNumbers = (data) => {
      setShiftNumbers(data);
      updateIsLoadingScreen(false);
    };

    socket.on('closeShift', handleCloseShift);
    socket.on('checkShiftAndDayNumbers', handleCheckShiftAndDayNumbers);

    return () => {
      socket.off('closeShift', handleCloseShift);
      socket.off('checkShiftAndDayNumbers', handleCheckShiftAndDayNumbers);
    };
  }, [socket]);

  const handleCancel = () => {
    updateShowModalEndShift(false);
  };

  const handleChoose = (type) => {
    setCloseType(type);
    setStep('confirm');
  };

  const handleBack = () => {
    setStep('choice');
    setCloseType(null);
    setCashOnClosing('');
  };

  const handleFinishConfirm = () => {
    if (closeType === 'day' && cashOnClosing === '') {
      toast.error(t('error.errorCashOnClosing'));
      return;
    }

    updateIsLoadingScreen(true);
    socket.emit('closeShift', {
      action: 'closeShift',
      bingoName: hallRef.current,
      operator: operatorRef.current,
      session: sessionRef.current,
      printer: printerRef.current,
      isLastOfTheDay: closeType === 'day',
      cashOnClosing: closeType === 'day' ? Number(cashOnClosing) : null,
    });
  };

  const fmt = (v) =>
    v !== undefined && v !== null ? Number(v).toFixed(2) : '0.00';

  const tableStyles = {
    '& thead th': {
      fontWeight: 'bold',
      backgroundColor: '#f0f0f0',
      color: '#000',
    },
    '& tbody tr:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
  };

  const cellSx = { color: '#000', py: '4px' };

  const renderProviderRows = (section, isKiosk = false, isTotal = false) => {
    const chTotalData = isTotal ? {
      cashIn: (section?.ch?.cashIn ?? 0) + (section?.chSpillerkonto?.cashIn ?? 0),
      cashOut: (section?.ch?.cashOut ?? 0) + (section?.chSpillerkonto?.cashOut ?? 0),
      neto: (section?.ch?.neto ?? 0) + (section?.chSpillerkonto?.neto ?? 0),
    } : null;

    const providers = [
      { key: 'CH', data: section?.ch },
      { key: 'CH Spillerkonto', data: section?.chSpillerkonto },
      ...(isTotal ? [{ key: 'CH Total', data: chTotalData, bold: true }] : []),
      { key: 'OK', data: section?.ok },
      { key: 'WG', data: section?.wg },
      ...(!isKiosk ? [{ key: 'NT', data: section?.nt }] : []),
      ...(!isKiosk ? [{ key: 'Kiosk', data: section?.kiosk }] : []),
    ];

    return (
      <>
        {providers.map(({ key, data, bold }) => (
          <TableRow key={key}>
            <TableCell sx={{ ...cellSx, ...(bold && { fontWeight: 'bold' }) }}>{key}</TableCell>
            <TableCell align="right" sx={{ ...cellSx, ...(bold && { fontWeight: 'bold' }) }}>
              {fmt(data?.cashIn)}
            </TableCell>
            <TableCell align="right" sx={{ ...cellSx, ...(bold && { fontWeight: 'bold' }) }}>
              {fmt(data?.cashOut)}
            </TableCell>
            <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>
              {fmt(data?.neto)}
            </TableCell>
          </TableRow>
        ))}
        {!isKiosk && (
          <TableRow key="refunds">
            <TableCell sx={cellSx}>{t('endShift.refunds')}</TableCell>
            <TableCell align="right" sx={cellSx}>
              0.00
            </TableCell>
            <TableCell align="right" sx={cellSx}>
              {fmt(section?.refunds)}
            </TableCell>
            <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>
              {fmt(-(section?.refunds ?? 0))}
            </TableCell>
          </TableRow>
        )}
        {!isKiosk && (
          <TableRow key="paymentCash">
            <TableCell sx={cellSx}>{t('endShift.paymentCash')}</TableCell>
            <TableCell align="right" sx={cellSx}>
              {fmt(section?.payments?.cashIn)}
            </TableCell>
            <TableCell align="right" sx={cellSx}>
              {fmt(section?.payments?.cashOut)}
            </TableCell>
            <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>
              {fmt(
                (section?.payments?.cashIn ?? 0) -
                  (section?.payments?.cashOut ?? 0)
              )}
            </TableCell>
          </TableRow>
        )}
        <TableRow key="paymentCard">
          <TableCell sx={cellSx}>{t('endShift.paymentCard')}</TableCell>
          <TableCell align="right" sx={cellSx}>
            {fmt(section?.payments?.card)}
          </TableCell>
          <TableCell align="right" sx={cellSx}>
            0.00
          </TableCell>
          <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>
            {fmt(section?.payments?.card)}
          </TableCell>
        </TableRow>
      </>
    );
  };

  const renderTableSection = (sectionData, label, isKiosk = false, isTotal = false) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 'bold', mb: 0.5, color: '#000', textAlign: 'center' }}
      >
        {label}
      </Typography>
      <TableContainer>
        <Table size="small" sx={tableStyles}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#000' }}></TableCell>
              <TableCell align="right" sx={{ color: '#000' }}>
                {t('endShift.cashin')}
              </TableCell>
              <TableCell align="right" sx={{ color: '#000' }}>
                {t('endShift.cashout')}
              </TableCell>
              <TableCell align="right" sx={{ color: '#000' }}>
                {t('endShift.neto')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderProviderRows(sectionData, isKiosk, isTotal)}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const periodData =
    activeTab === 0 ? shiftNumbers?.currentShift : shiftNumbers?.day;

  return (
    <>
      {isLoadingScreen ? (
        <></>
      ) : (
        <div id="react-confirm-alert">
          <div className="react-confirm-alert-overlay">
            <div className="react-confirm-alert">
              {/* ── STEP 1: CHOICE ── */}
              {step === 'choice' && (
                <div
                  className="react-confirm-alert-body"
                  style={{ width: '700px' }}
                >
                  <h1>{t('endShift.choiceTitle')}</h1>
                  <p>{t('endShift.choiceSubtitle')}</p>
                  <div className="react-confirm-alert-button-group">
                    <button
                      className="btn-custom-yes"
                      style={{
                        width: '100%',
                        fontSize: '18px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                      onClick={() => handleChoose('shift')}
                    >
                      <ExitToAppIcon style={{ fontSize: '1.2em' }} />
                      {t('endShift.closeShift')}
                    </button>
                    <button
                      className="btn-custom-yes-and-stay"
                      style={{
                        width: '100%',
                        fontSize: '18px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                      onClick={() => handleChoose('day')}
                    >
                      <CalendarTodayIcon style={{ fontSize: '1.2em' }} />
                      {t('endShift.closeDay')}
                    </button>
                    <button
                      className="btn-custom-no"
                      style={{
                        width: '100%',
                        fontSize: '18px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                      onClick={handleCancel}
                    >
                      <CancelIcon style={{ fontSize: '1.2em' }} />
                      {t('sales.cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 2: CONFIRM ── */}
              {step === 'confirm' && (
                <div
                  className="react-confirm-alert-body"
                  style={{ width: '1500px' }}
                >
                  <h1>{t('endShift.title')}</h1>
                  <p>{t('endShift.subtitle')}</p>
                  <Paper
                    elevation={2}
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Tabs
                      value={activeTab}
                      onChange={(_, v) => setActiveTab(v)}
                      sx={{
                        borderBottom: '1px solid #ccc',
                        backgroundColor: '#f0f0f0',
                        '& .MuiTab-root': { color: '#555' },
                        '& .MuiTab-root.Mui-selected': {
                          color: '#000',
                          fontWeight: 'bold',
                        },
                        '& .MuiTabs-indicator': { backgroundColor: '#000' },
                      }}
                    >
                      <Tab label={t('endShift.currentShift')} />
                      <Tab label={t('endShift.day')} />
                    </Tabs>
                    <Box sx={{ display: 'flex', gap: 2, p: 1.5 }}>
                      {renderTableSection(
                        periodData?.cashier,
                        t('endShift.cashierSection')
                      )}
                      {renderTableSection(
                        periodData?.selfKiosk,
                        t('endShift.selfKioskSection'),
                        true
                      )}
                      {renderTableSection(
                        periodData?.total,
                        t('endShift.totalSection'),
                        false,
                        true
                      )}
                    </Box>
                  </Paper>

                  {closeType === 'day' && (
                    <TextField
                      autoComplete="off"
                      label={t('endShift.cashOnClosing') + '*'}
                      value={cashOnClosing}
                      variant="outlined"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^\d*$/.test(v)) setCashOnClosing(v);
                      }}
                      className="modal-textfield"
                      style={{ marginBottom: '16px' }}
                    />
                  )}

                  <div className="react-confirm-alert-button-group">
                    <button
                      className="btn-custom-yes"
                      onClick={handleFinishConfirm}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                      {t('sales.confirm')}
                    </button>
                    <button
                      className="btn-custom-no"
                      onClick={handleBack}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                      {t('endShift.back')}
                    </button>
                    <button
                      className="btn-custom-no"
                      onClick={handleCancel}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <CancelIcon style={{ fontSize: '1.2em' }} />
                      {t('sales.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ModalEndShiftAuto;
